import React from 'react'
import { useTheme } from 'styled-components'

// Hooks
import { usePrFileStatusChangesSubscriptionIfNeeded } from 'components/WebRendererCanvas/hooks'

// Components
import PageOriginContent from 'modules/shares/PageCanvasView/components/PageOriginContent/PageOriginContent'
import { WebRendererCanvas } from 'components/WebRendererCanvas'
import Artboard from 'modules/shares/PageCanvasView/components/ArtboardsInfoOverlay/Artboard'
import { PageCanvasSupportGate } from 'modules/shares/PageCanvasView/components/PageCanvasSupportGate'
import {
  CanvasRendererProvider,
  usePRFile,
  usePRFileArtboards,
} from '@sketch-hq/sketch-web-renderer'

// Local Components
import PublicationCanvasControls from '../PublicationCanvasControls'

// Styles
import { CanvasContainer } from './PublicationCanvas.styles'

// Types
import { PublicationPageFragment } from '@sketch/gql-types'
import { RenderStatus } from '@sketch/modules-common'

interface PublicationCanvasProps {
  shareIdentifier: string
  versionShortId: string
  page: PublicationPageFragment
  onPageSelected: (page: PublicationPageFragment) => void
  renderStatus: RenderStatus
}

const ArtboardNames: React.FC = () => {
  const prFile = usePRFile()
  const artboards = usePRFileArtboards() ?? []

  if (!prFile) return null

  return (
    <>
      {artboards.map(artboard => (
        <Artboard
          key={artboard.objectId}
          prFile={prFile}
          artboard={artboard}
          disablePointerCursor
          disableHover
        />
      ))}
    </>
  )
}

const PublicationCanvas: React.FC<PublicationCanvasProps> = ({
  shareIdentifier,
  versionShortId,
  page,
  renderStatus,
  onPageSelected,
}) => {
  const theme = useTheme()

  // Presentation File Status Subscription
  usePrFileStatusChangesSubscriptionIfNeeded({
    renderStatus,
    presentationFile: page.presentationFile,
    pageIdentifier: page.identifier,
  })

  if (!page.uuid) return null

  return (
    <CanvasRendererProvider>
      <PageCanvasSupportGate>
        <CanvasContainer>
          <WebRendererCanvas
            pageUUID={page.uuid}
            page={page}
            versionShortId={versionShortId}
            shareIdentifier={shareIdentifier}
            backgroundColor={theme.colors.background.secondary.B}
          >
            <PageOriginContent>
              <ArtboardNames />
            </PageOriginContent>
          </WebRendererCanvas>
          <PublicationCanvasControls
            identifier={shareIdentifier}
            page={page}
            onPageSelected={onPageSelected}
          />
        </CanvasContainer>
      </PageCanvasSupportGate>
    </CanvasRendererProvider>
  )
}

export default PublicationCanvas
